/**
 * Pure utility functions for the Model Settings UI.
 *
 * Extracted from SettingsModels.tsx for testability.
 *
 * @module @humanizer/studio/components/settings/settings-models-utils
 */

import type { RoleFilteredModel } from '../../types/electron';

/** Human-readable privacy label for a model */
export function privacyLabel(model: RoleFilteredModel): string {
  switch (model.privacyLevel) {
    case 'local':
      return 'Local';
    case 'cloud-trusted':
      return 'Cloud';
    default:
      return 'Third-Party';
  }
}

/** Group models into Recommended / Available / Unavailable for the picker */
export function groupByRecommendation(models: RoleFilteredModel[]): Array<{ label: string; models: RoleFilteredModel[] }> {
  const recommended: RoleFilteredModel[] = [];
  const available: RoleFilteredModel[] = [];
  const unavailable: RoleFilteredModel[] = [];

  for (const m of models) {
    if (!m.available) {
      unavailable.push(m);
    } else if (
      m.compatSummary?.recommendation === 'recommended' ||
      (m.vettingStatus === 'approved' && m.qualityScore >= 0.7)
    ) {
      recommended.push(m);
    } else {
      available.push(m);
    }
  }

  const groups: Array<{ label: string; models: RoleFilteredModel[] }> = [];
  if (recommended.length > 0) groups.push({ label: 'Recommended', models: recommended });
  if (available.length > 0) groups.push({ label: 'Available', models: available });
  if (unavailable.length > 0) groups.push({ label: 'Unavailable', models: unavailable });
  return groups;
}

/** Format cost per 1M tokens for display (e.g. "$3/1M in · $15/1M out") */
export function formatCost(cost: { input: number; output: number }): string {
  if (cost.input === 0 && cost.output === 0) return 'Free';
  const fmtNum = (n: number) => {
    // costPer1kTokens is per 1k tokens; convert to per 1M for display
    const perMillion = n * 1000;
    return perMillion >= 1 ? `$${perMillion.toFixed(perMillion % 1 === 0 ? 0 : 1)}` : `$${perMillion.toFixed(2)}`;
  };
  return `${fmtNum(cost.input)}/1M in \u00B7 ${fmtNum(cost.output)}/1M out`;
}

/** Build a rich option label for the model picker */
export function richModelLabel(m: RoleFilteredModel): string {
  const parts = [m.name];
  const costStr = formatCost(m.costPer1kTokens);
  if (costStr !== 'Free' && m.backend !== 'ollama') {
    parts.push(`\u2014 ${costStr}`);
  } else if (m.backend === 'ollama') {
    parts.push('\u2014 Free');
  }
  parts.push(`\u00B7 ${privacyLabel(m)}`);
  if (m.vettingStatus && m.vettingStatus !== 'approved') parts.push('[unvetted]');
  if (!m.available && m.unavailableReason) parts.push(`\u2014 ${m.unavailableReason}`);
  return parts.join(' ');
}
